import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router";
import { Button, ButtonGroup } from "@datapay-by-datacom/design-system";
import { WorkflowActionBar } from "../../shared";
import { CompanyShellPreview } from "../bulk-create-redesign/CompanyShellPreview";
import {
  applyCompanyShellDraft,
  type CompanyShellDraft,
} from "../bulk-create-redesign/companyShellEditorDraft";
import { useBulkCreateFlow } from "../bulk-create-redesign/bulkCreateFlow";
import {
  CommitCompaniesConfirmModal,
  CompanyRowsTable,
  CompanyShellExtractionNotice,
  SelectedCompanyConfigTemplatePreview,
} from "../components";
import { companyCellEditKey } from "../components/companyCellEditKey";
import {
  buildCompanyShellRow,
  cellErrorKey,
  formatCompanyShellRowForPreview,
  isKnownShellCsvHeader,
  mapCompanyShellClientIssues,
  mapValidationErrorsToCells,
  unknownShellCsvColumnCellKey,
  unknownShellCsvColumnMessage,
  useCommitCompanyShellProvision,
  useCompanyConfigVersion,
  useCompanyProvisionAgentId,
  useCompanyProvisionWizard,
  useValidateCompanyShellProvision,
} from "../hooks";
import type {
  CompanyShellWorkloadsManagerReturn,
  MappedValidationErrors,
} from "../hooks";
import type {
  CompanyShellRow,
} from "../../generated/core-api";
import type {
  CompanyShellCsvColumn,
  CompanyShellValidateResult,
} from "../types";
import s from "./BulkCreateCompanyReviewScreen.module.css";

/**
 * Review section in the combined bulk company-creation screen. Shows the
 * parsed rows as editable fields, runs the `mode: "validate"` invocation on
 * demand (highlighting errored cells red with an accessible message), and
 * enables `mode: "commit"` only once a validate run comes back clean.
 *
 * Below the editable grid, a read-only preview of the selected template's
 * values renders via `ProfileSummary` — the same grouped ("Datapay Reasons" /
 * "Direct Access Reasons") display used on the version-history screen — so a
 * PS user can confirm what the chosen template applies before committing.
 *
 * Validate/Commit call the real `company_shell_provision` agent invocation —
 * see `useCompanyProvisionAgentId` — but that agent doesn't exist in any
 * environment yet (Pacific.Agentic.Api#834 is still blocked), so both
 * actions render disabled with an explanatory message until the env var is
 * configured.
 *
 * Step 16 (LADR-039 / #440): `FeatureFlag.EnableWorkloadsCutover` (read by
 * `BulkCreateCompanyWizardProvider`, not here) switches which track backs
 * Validate/Commit — Track 1 above (`useValidateCompanyShellProvision`/
 * `useCommitCompanyShellProvision`, this screen's original implementation)
 * or Track 2 (`useCompanyShellWorkloadsManager`, Slice 5 workloads REST — no
 * onboarding agent required). This component dispatches on `shellManager`
 * from wizard context (`null` on Track 1, an instance on Track 2) rather
 * than re-reading the flag itself, mirroring the Step 9/13 wrapper
 * precedent: exactly one of the two variants mounts, and each calls only
 * its own track's hook(s).
 *
 * Track 2 does not yet support this screen's `embedded`/redesign
 * (`CompanyShellPreview` card-editor) affordances — it renders its own
 * standalone header/table regardless of `flow.presentation` or the
 * `embedded`/`provisioningOptions` props a redesign-mode caller passes.
 * This is a known, documented gap (not a third behaviour): the two feature
 * flags (`EnableWorkloadsCutover` and the bulk-create redesign) are not
 * expected to be enabled together before Track 2 grows redesign support:
 * see `.plans/ladr-039-manual-followups.md`.
 */
export interface BulkCreateCompanyReviewScreenProps {
  /** Render as the Review section inside the combined Upload & Review page. Track 1 only — see module doc. */
  embedded?: boolean;
  /** Environment/template controls placed immediately after the company form. Track 1 only — see module doc. */
  provisioningOptions?: ReactNode;
  /** Reports whether the redesign's whole-company editor is open. Track 1 only — see module doc. */
  onCompanyEditingChange?: (isEditing: boolean) => void;
  /** Reports validation/commit activity so an embedding upload screen can lock replacement controls. Track 1 only — see module doc. */
  onOperationBusyChange?: (isBusy: boolean) => void;
  /** Locks redesign actions while the parent replaces or parses the CSV. Track 1 only — see module doc. */
  isExternallyBusy?: boolean;
}

export function BulkCreateCompanyReviewScreen(
  props: BulkCreateCompanyReviewScreenProps = {}
) {
  const { shellManager } = useCompanyProvisionWizard();
  return shellManager ? (
    <BulkCreateCompanyReviewScreenWorkloads manager={shellManager} />
  ) : (
    <BulkCreateCompanyReviewScreenTrackOne {...props} />
  );
}

/** AC-UPLOAD-12 / R-UPLOAD-2: undeclared CSV headers never reach the API payload. Track 2 only — Track 1 folds this into its own `clientIssues`/`errorMap` merge below. */
function computeUnknownColumns(
  columns: CompanyShellCsvColumn[]
): CompanyShellCsvColumn[] {
  return columns.filter((c) => !isKnownShellCsvHeader(c.header));
}

/**
 * Maps a validate result's row-index errors onto grid cells, then injects a
 * blocking Fail on every unknown-column cell (client-side only — the API
 * never sees those columns, so it can't report failures for them itself).
 * Track 2 only: Track 1's richer `errorMap` above also merges in
 * `mapCompanyShellClientIssues` (client-only CSV parse/required-field
 * issues), which Track 2's manager has no equivalent of yet.
 */
function computeErrorMap(
  rows: CompanyShellRow[],
  unknownColumns: CompanyShellCsvColumn[],
  validateResult: CompanyShellValidateResult | null
): MappedValidationErrors {
  const mapped = mapValidationErrorsToCells(
    validateResult?.errors ?? [],
    rows.length
  );
  if (!validateResult || unknownColumns.length === 0) {
    return mapped;
  }
  for (let i = 0; i < rows.length; i++) {
    for (const column of unknownColumns) {
      const message = unknownShellCsvColumnMessage(column.header);
      const key = cellErrorKey(i, unknownShellCsvColumnCellKey(column.header));
      const cellExisting = mapped.cellErrors.get(key);
      if (cellExisting) cellExisting.push(message);
      else mapped.cellErrors.set(key, [message]);
      const rowExisting = mapped.errorsByRow.get(i);
      if (rowExisting) rowExisting.push(message);
      else mapped.errorsByRow.set(i, [message]);
    }
  }
  return mapped;
}

function computeBlockingIssueCount(
  validateResult: CompanyShellValidateResult | null,
  unknownColumns: ReturnType<typeof computeUnknownColumns>,
  rowCount: number
): number {
  if (!validateResult) return 0;
  let count = validateResult.errors.filter(
    (error) => error.severity !== "warning"
  ).length;
  if (unknownColumns.length > 0 && rowCount > 0) {
    count += unknownColumns.length * rowCount;
  }
  return count;
}

/** Track 1 (flag off / default): `useValidateCompanyShellProvision`/`useCommitCompanyShellProvision`, gated on the `company_shell_provision` agent. */
function BulkCreateCompanyReviewScreenTrackOne({
  embedded = false,
  provisioningOptions,
  onCompanyEditingChange,
  onOperationBusyChange,
  isExternallyBusy = false,
}: BulkCreateCompanyReviewScreenProps = {}) {
  const navigate = useNavigate();
  const flow = useBulkCreateFlow();
  const { state, updateState, isNavigationBlocked } =
    useCompanyProvisionWizard();
  const {
    agentId,
    serviceAccountId,
    isConfigured,
    isLoading: isAgentLoading,
  } = useCompanyProvisionAgentId();

  const validateInvocation = useValidateCompanyShellProvision();
  const commitInvocation = useCommitCompanyShellProvision();
  const validationSummaryRef = useRef<HTMLDivElement>(null);
  const validationErrorRef = useRef<HTMLParagraphElement>(null);
  const commitErrorRef = useRef<HTMLParagraphElement>(null);
  const [shouldRevealValidationSummary, setShouldRevealValidationSummary] =
    useState(false);
  const [focusFirstIssueRequest, setFocusFirstIssueRequest] = useState(0);
  const [commitConfirmOpen, setCommitConfirmOpen] = useState(false);
  const [isCompanyEditing, setIsCompanyEditing] = useState(false);
  const validationRevisionRef = useRef(state.validationInputRevision);
  const isRedesign = flow.presentation === "cards";
  const handleCompanyEditingChange = (isEditing: boolean) => {
    setIsCompanyEditing(isEditing);
    onCompanyEditingChange?.(isEditing);
  };

  const {
    data: templateVersion,
    isLoading: isTemplateVersionLoading,
    isError: isTemplateVersionError,
  } = useCompanyConfigVersion(
    state.template?.config_code,
    state.template?.version
  );

  useEffect(() => {
    if (validateInvocation.result) {
      const resultIsStale =
        validationRevisionRef.current !== state.validationInputRevision;
      updateState({
        validateResult: validateInvocation.result,
        validateStale: resultIsStale,
      });
      setShouldRevealValidationSummary(true);
    }
    // updateState is stable in the production provider; excluding it avoids
    // re-applying the same result in lightweight test providers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.validationInputRevision,
    validateInvocation.result,
  ]);

  useEffect(() => {
    if (isNavigationBlocked) setCommitConfirmOpen(false);
  }, [isNavigationBlocked]);

  useEffect(() => {
    if (!shouldRevealValidationSummary || !state.validateResult) return;

    const summary = validationSummaryRef.current;
    if (!summary) return;

    if (state.validateResult.errors.length > 0) {
      summary.focus({ preventScroll: true });
    }
    summary.scrollIntoView?.({ behavior: "smooth", block: "start" });
    setShouldRevealValidationSummary(false);
  }, [shouldRevealValidationSummary, state.validateResult]);

  useEffect(() => {
    if (!validateInvocation.error) return;
    validationErrorRef.current?.scrollIntoView?.({
      behavior: "smooth",
      block: "start",
    });
  }, [validateInvocation.error]);

  useEffect(() => {
    if (!commitInvocation.error) return;
    commitErrorRef.current?.scrollIntoView?.({
      behavior: "smooth",
      block: "start",
    });
  }, [commitInvocation.error]);

  useEffect(() => {
    if (commitInvocation.workflowId) {
      updateState({ commitWorkflowId: commitInvocation.workflowId });
      navigate(flow.resultsPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commitInvocation.workflowId]);

  useEffect(() => {
    if (state.commitWorkflowId && !commitInvocation.workflowId) {
      navigate(flow.resultsPath, { replace: true });
    }
  }, [
    commitInvocation.workflowId,
    flow.resultsPath,
    navigate,
    state.commitWorkflowId,
  ]);

  const unknownColumns = useMemo(
    () => state.columns.filter((c) => !isKnownShellCsvHeader(c.header)),
    [state.columns]
  );

  const clientIssues = useMemo(
    () =>
      mapCompanyShellClientIssues(
        state.rows,
        state.columns,
        state.parseErrors,
        state.rowCsvLineNumbers
      ),
    [
      state.columns,
      state.parseErrors,
      state.rowCsvLineNumbers,
      state.rows,
    ]
  );

  const errorMap = useMemo(() => {
    const serverIssues = mapValidationErrorsToCells(
      state.validateResult?.errors ?? [],
      state.rows.length
    );
    const mapped = {
      cellErrors: new Map(
        [...clientIssues.cellErrors].map(([key, messages]) => [
          key,
          [...messages],
        ])
      ),
      rowErrors: new Map(
        [...clientIssues.rowErrors].map(([key, messages]) => [
          key,
          [...messages],
        ])
      ),
      errorsByRow: new Map(
        [...clientIssues.errorsByRow].map(([key, messages]) => [
          key,
          [...messages],
        ])
      ),
      batchErrors: [...clientIssues.batchErrors],
    };
    const appendUnique = <K,>(
      map: Map<K, string[]>,
      key: K,
      messages: string[]
    ) => {
      const existing = map.get(key) ?? [];
      for (const message of messages) {
        if (!existing.includes(message)) existing.push(message);
      }
      map.set(key, existing);
    };
    for (const [key, messages] of serverIssues.cellErrors) {
      appendUnique(mapped.cellErrors, key, messages);
    }
    for (const [key, messages] of serverIssues.rowErrors) {
      appendUnique(mapped.rowErrors, key, messages);
    }
    for (const [key, messages] of serverIssues.errorsByRow) {
      appendUnique(mapped.errorsByRow, key, messages);
    }
    for (const message of serverIssues.batchErrors) {
      if (!mapped.batchErrors.includes(message)) {
        mapped.batchErrors.push(message);
      }
    }

    // Client-side AC-UPLOAD-12: undeclared headers never reach the API
    // payload, so inject a blocking Fail on each unknown column line after
    // Validate — otherwise ExtraField would show Pass and Commit would open.
    if (!state.validateResult || unknownColumns.length === 0) {
      return mapped;
    }
    for (let i = 0; i < state.rows.length; i++) {
      for (const column of unknownColumns) {
        const message = unknownShellCsvColumnMessage(column.header);
        const key = cellErrorKey(
          i,
          unknownShellCsvColumnCellKey(column.header)
        );
        const cellExisting = mapped.cellErrors.get(key);
        if (cellExisting) cellExisting.push(message);
        else mapped.cellErrors.set(key, [message]);
        const rowExisting = mapped.errorsByRow.get(i);
        if (rowExisting) rowExisting.push(message);
        else mapped.errorsByRow.set(i, [message]);
      }
    }
    return mapped;
  }, [
    clientIssues,
    state.validateResult,
    state.rows.length,
    unknownColumns,
  ]);

  const blockingIssueCount = useMemo(() => {
    let count = errorMap.batchErrors.length;
    for (const messages of errorMap.errorsByRow.values()) {
      count += messages.length;
    }
    return count;
  }, [errorMap]);

  const clientIssueCount = useMemo(() => {
    let count = clientIssues.batchErrors.length;
    for (const messages of clientIssues.errorsByRow.values()) {
      count += messages.length;
    }
    return count;
  }, [clientIssues]);
  const hasWarningSummary = blockingIssueCount > 0 || state.validateStale;
  const editedCellKeys = useMemo(
    () => new Set(state.editedCellKeys),
    [state.editedCellKeys]
  );

  const handleCellChange = (
    rowArrayIndex: number,
    header: string,
    value: string
  ) => {
    const currentDisplay = formatCompanyShellRowForPreview(
      state.rows[rowArrayIndex]
    );
    const nextDisplay = { ...currentDisplay, [header]: value };
    const nextRow = buildCompanyShellRow((h) => nextDisplay[h] ?? "");
    const nextRows = [...state.rows];
    nextRows[rowArrayIndex] = nextRow;
    // Keep the existing validate errors on screen so the user can still see
    // which columns to fix — but mark the result stale so Commit can't fire
    // against edited-but-unvalidated data until they re-run Validate.
    updateState({
      rows: nextRows,
      editedCellKeys: state.editedCellKeys.includes(
        companyCellEditKey(rowArrayIndex, header)
      )
        ? state.editedCellKeys
        : [
            ...state.editedCellKeys,
            companyCellEditKey(rowArrayIndex, header),
          ],
      validateStale: state.validateResult !== null,
      validationInputRevision: state.validationInputRevision + 1,
    });
  };

  const handleCompanySave = (
    rowArrayIndex: number,
    draft: CompanyShellDraft
  ) => {
    updateState(
      applyCompanyShellDraft({
        rows: state.rows,
        columns: state.columns,
        rowIndex: rowArrayIndex,
        draft,
        editedCellKeys: state.editedCellKeys,
        hasValidationResult: state.validateResult !== null,
        validationInputRevision: state.validationInputRevision,
      })
    );
  };

  const handleValidate = () => {
    if (!agentId || !serviceAccountId || !state.template || !state.mypayEnvironment)
      return;
    validationRevisionRef.current = state.validationInputRevision;
    validateInvocation.invoke({
      agentId,
      serviceAccountId,
      template: state.template,
      rows: state.rows,
      mypayEnvironment: state.mypayEnvironment,
    });
  };

  const confirmCommit = () => {
    if (
      state.commitWorkflowId ||
      !agentId ||
      !serviceAccountId ||
      !state.template ||
      !state.mypayEnvironment
    )
      return;
    setCommitConfirmOpen(false);
    commitInvocation.invoke({
      agentId,
      serviceAccountId,
      template: state.template,
      rows: state.rows,
      mypayEnvironment: state.mypayEnvironment,
    });
  };

  const isValidating =
    validateInvocation.isInvoking || validateInvocation.isPolling;
  const isCommitting =
    commitInvocation.isInvoking || commitInvocation.isPolling;
  const hasCommitStarted = state.commitWorkflowId !== null;

  useEffect(() => {
    onOperationBusyChange?.(
      isValidating || isCommitting || hasCommitStarted
    );
  }, [
    hasCommitStarted,
    isCommitting,
    isValidating,
    onOperationBusyChange,
  ]);
  // Freeze the grid while a validate or commit is in flight. If the user
  // edited a row mid-validate, the completed validateResult would describe
  // the pre-edit rows, yet the sync effect above would still clear
  // validateStale — wrongly re-enabling Commit against edited-but-unvalidated
  // data. Making the grid read-only for the duration closes that window.
  const isBusy =
    isValidating || isCommitting || (isRedesign && isExternallyBusy);

  const hasReadyServiceAccount = isConfigured && !!serviceAccountId;
  const canValidate =
    hasReadyServiceAccount &&
    !!state.template &&
    !!state.mypayEnvironment &&
    state.rows.length > 0 &&
    clientIssueCount === 0 &&
    !hasCommitStarted &&
    (!isRedesign || !isCompanyEditing) &&
    !isValidating &&
    !isCommitting &&
    (!isRedesign || !isExternallyBusy);
  const canCommit =
    hasReadyServiceAccount &&
    !!state.template &&
    !!state.mypayEnvironment &&
    !!state.validateResult &&
    // Warnings (severity === "warning") are informational — do not block Commit.
    // Track 2 ValidationReport.warnings are merged into errors[] for display
    // only; see companyShellWorkloadsResultMapper.
    !state.validateResult.errors.some(
      (error) => error.severity !== "warning"
    ) &&
    clientIssueCount === 0 &&
    !hasCommitStarted &&
    // Undeclared CSV headers never reach the API — still block Commit
    // (AC-UPLOAD-12 / R-UPLOAD-2).
    unknownColumns.length === 0 &&
    // Edited since the last validate run — must re-validate before committing.
    !state.validateStale &&
    (!isRedesign || !isCompanyEditing) &&
    !isValidating &&
    !isCommitting &&
    (!isRedesign || !isExternallyBusy);
  const companyLabel = state.rows.length === 1 ? "company" : "companies";
  const operationStatus = isValidating
    ? `Validating ${state.rows.length} ${companyLabel}…`
    : isCommitting
      ? `Starting creation for ${state.rows.length} ${companyLabel}…`
      : null;
  const commitDisabledReason = canCommit || operationStatus
    ? null
    : isRedesign && isCompanyEditing
      ? "Save or cancel company editing before validating or creating companies."
      : state.validateStale
      ? "Company data or provisioning options changed. Validate again before creating companies."
        : !state.validateResult
          ? "Validate the companies before creating them."
          : state.validateResult.errors.length > 0 ||
              clientIssueCount > 0 ||
              unknownColumns.length > 0
            ? "Fix validation errors, then validate again before creating companies."
            : "Company creation is unavailable while the current operation completes.";
  const validationRequirements = [
    state.mypayEnvironment ? null : "Select an environment",
    state.template ? null : "Select a template",
    clientIssues.requiredIssueCount > 0
      ? `Complete ${clientIssues.requiredIssueCount} required field${
          clientIssues.requiredIssueCount === 1 ? "" : "s"
        }`
      : null,
    clientIssues.duplicateCompanyCodeIssueCount > 0
      ? `Resolve ${clientIssues.duplicateCompanyCodeIssueCount} duplicate Company Code${
          clientIssues.duplicateCompanyCodeIssueCount === 1 ? "" : "s"
        } before validating`
      : null,
    clientIssues.batchErrors.length > 0 ? "Fix CSV structure issues" : null,
  ].filter((requirement): requirement is string => requirement !== null);
  const validateDisabledGuidanceId = !isConfigured
    ? "company-provision-unavailable"
    : operationStatus
      ? "company-operation-status"
      : validationRequirements.length > 0
        ? "company-validation-guidance"
        : isRedesign && isCompanyEditing
          ? "company-commit-guidance"
          : undefined;

  if (embedded && state.rows.length === 0) {
    return null;
  }

  if (!embedded && !isAgentLoading && state.rows.length === 0) {
    return (
      <div className={s.container}>
        <header className={s.header}>
          <h1 className={s.title}>Review</h1>
          <p className={s.subtitle}>
            No companies to review. Please upload a CSV first.
          </p>
        </header>
        <ButtonGroup align="right">
          <Button
            variant="tertiary"
            onPress={() => navigate(flow.uploadPath)}
          >
            Back to upload
          </Button>
        </ButtonGroup>
      </div>
    );
  }

  return (
    <div
      id={embedded ? "company-review" : undefined}
      className={`${s.container} ${embedded ? s.embedded : ""}`}
      aria-labelledby={embedded ? "company-review-heading" : undefined}
    >
      <header className={s.header}>
        {embedded ? (
          <h2 id="company-review-heading" className={s.title}>
            Review companies
          </h2>
        ) : (
          <h1 className={s.title}>Review</h1>
        )}
        <p className={s.subtitle}>
          Review and edit company details, then validate the batch before
          creating companies.
        </p>
      </header>

      <CompanyShellExtractionNotice provenance={state.extraction} />

      {!isConfigured && (
        <p
          id="company-provision-unavailable"
          className={s.warning}
          role="alert"
        >
          Validating and creating companies isn&apos;t available in this
          environment yet.
        </p>
      )}
      {validateInvocation.error && (
        <p
          ref={validationErrorRef}
          className={s.warning}
          role="alert"
        >
          {validateInvocation.error}
        </p>
      )}
      {commitInvocation.error && (
        <p ref={commitErrorRef} className={s.warning} role="alert">
          {commitInvocation.error}
        </p>
      )}
      {(blockingIssueCount > 0 || state.validateResult) && (
          <div
            ref={validationSummaryRef}
            className={`${hasWarningSummary ? s.warning : s.okBanner} ${s.validationSummary}`}
            role={hasWarningSummary ? "region" : "status"}
            aria-labelledby="company-validation-summary"
            tabIndex={-1}
          >
            <p id="company-validation-summary" className={s.validationMessage}>
              {blockingIssueCount > 0
                ? `${blockingIssueCount} issue${blockingIssueCount !== 1 ? "s" : ""} found across ${state.rows.length} ${
                    state.rows.length === 1
                      ? "company"
                      : "companies"
                  }.`
                : state.validateStale
                  ? "Company data or provisioning options changed. Validate again."
                : "All companies passed validation."}
            </p>
            {clientIssueCount > 0 && (
              <p className={s.validationMessage}>
                Fix the highlighted fields below before validating, or update
                the CSV and upload it again.
              </p>
            )}
            {state.validateStale && (
              <p className={s.validationMessage}>
                Company data or provisioning options changed since the last
                validation. Some highlighted issues may reflect the previous
                values — re-run Validate before creating companies.
              </p>
            )}
            {errorMap.batchErrors.map((message, i) => (
              <p key={i} className={s.validationMessage}>
                {message}
              </p>
            ))}
            {blockingIssueCount > 0 &&
              errorMap.errorsByRow.size > 0 &&
              flow.presentation === "cards" && (
                <Button
                  variant="tertiary"
                  isDisabled={isCompanyEditing || isBusy}
                  onPress={() =>
                    setFocusFirstIssueRequest((request) => request + 1)
                  }
                >
                  View first issue
                </Button>
              )}
          </div>
      )}

      {flow.presentation === "cards" ? (
        <CompanyShellPreview
          columns={state.columns}
          rows={state.rows}
          editable
          isDisabled={isBusy}
          onCompanySave={handleCompanySave}
          onEditingChange={handleCompanyEditingChange}
          suppressDiscardConfirmation={isNavigationBlocked}
          cellErrors={blockingIssueCount > 0 ? errorMap.cellErrors : undefined}
          errorsByRow={blockingIssueCount > 0 ? errorMap.errorsByRow : undefined}
          editedCellKeys={editedCellKeys}
          rowCsvLineNumbers={state.rowCsvLineNumbers}
          normalisations={
            state.validateResult ? state.validateResult.normalisations : undefined
          }
          focusFirstIssueRequest={focusFirstIssueRequest}
          ariaLabel="Company review"
        />
      ) : (
        <CompanyRowsTable
          columns={state.columns}
          rows={state.rows}
          editable={!isBusy}
          onCellChange={handleCellChange}
          cellErrors={blockingIssueCount > 0 ? errorMap.cellErrors : undefined}
          errorsByRow={blockingIssueCount > 0 ? errorMap.errorsByRow : undefined}
          editedCellKeys={editedCellKeys}
          normalisations={
            state.validateResult ? state.validateResult.normalisations : undefined
          }
          ariaLabel="Editable company rows"
        />
      )}

      {provisioningOptions}

      {state.template && (
        <SelectedCompanyConfigTemplatePreview
          templateVersion={templateVersion}
          isLoading={isTemplateVersionLoading}
          isError={isTemplateVersionError}
          presentation={flow.presentation === "cards" ? "review" : "compact"}
        />
      )}

      {isRedesign ? (
        <WorkflowActionBar
          ariaLabel="Company creation actions"
          status={
            operationStatus ||
            validationRequirements.length > 0 ||
            commitDisabledReason ? (
              <div className={s.actionMessages}>
                {operationStatus && (
                  <p
                    id="company-operation-status"
                    className={s.actionStatus}
                    role="status"
                  >
                    {operationStatus}
                  </p>
                )}
                {validationRequirements.length > 0 && (
                  <p
                    id="company-validation-guidance"
                    className={s.actionStatus}
                    role="status"
                  >
                    {validationRequirements.join(" · ")}
                  </p>
                )}
                {commitDisabledReason && (
                  <p id="company-commit-guidance" className={s.actionStatus}>
                    {commitDisabledReason}
                  </p>
                )}
              </div>
            ) : undefined
          }
          leadingActions={
            !embedded ? (
              <Button
                variant="tertiary"
                onPress={() => navigate(flow.uploadPath)}
              >
                Back to upload
              </Button>
            ) : undefined
          }
          trailingActions={
            <ButtonGroup align="right">
              <Button
                variant="secondary"
                isDisabled={!canValidate}
                aria-describedby={validateDisabledGuidanceId}
                onPress={handleValidate}
              >
                {isValidating ? "Validating…" : "Validate"}
              </Button>
              <Button
                variant="primary"
                isDisabled={!canCommit}
                aria-describedby={
                  commitDisabledReason ? "company-commit-guidance" : undefined
                }
                onPress={() => setCommitConfirmOpen(true)}
              >
                {isCommitting ? "Creating…" : "Create companies"}
              </Button>
            </ButtonGroup>
          }
        />
      ) : (
        <div className={s.actionBar}>
          {(operationStatus ||
            validationRequirements.length > 0 ||
            commitDisabledReason) && (
            <div className={s.actionMessages}>
              {operationStatus && (
                <p className={s.actionStatus} role="status">
                  {operationStatus}
                </p>
              )}
              {validationRequirements.length > 0 && (
                <p className={s.actionStatus} role="status">
                  {validationRequirements.join(" · ")}
                </p>
              )}
              {commitDisabledReason && (
                <p id="company-commit-guidance" className={s.actionStatus}>
                  {commitDisabledReason}
                </p>
              )}
            </div>
          )}
          <div className={s.actionControls}>
            <ButtonGroup align="right">
              {!embedded && (
                <Button
                  variant="tertiary"
                  onPress={() => navigate(flow.uploadPath)}
                >
                  Back to upload
                </Button>
              )}
              <Button
                variant="secondary"
                isDisabled={!canValidate}
                onPress={handleValidate}
              >
                {isValidating ? "Validating…" : "Validate"}
              </Button>
              <Button
                variant="primary"
                isDisabled={!canCommit}
                aria-describedby={
                  commitDisabledReason ? "company-commit-guidance" : undefined
                }
                onPress={() => setCommitConfirmOpen(true)}
              >
                {isCommitting ? "Creating…" : "Create companies"}
              </Button>
            </ButtonGroup>
          </div>
        </div>
      )}
      {state.template && state.mypayEnvironment && (
        <CommitCompaniesConfirmModal
          isOpen={commitConfirmOpen && !isNavigationBlocked}
          companyCount={state.rows.length}
          environment={state.mypayEnvironment}
          template={state.template}
          templateDisplayName={
            isRedesign ? templateVersion?.display_name : undefined
          }
          onCancel={() => setCommitConfirmOpen(false)}
          onConfirm={confirmCommit}
        />
      )}
    </div>
  );
}

/** Track 2 (flag on): `useCompanyShellWorkloadsManager` — Slice 5 workloads REST, no onboarding agent required. See module doc for the redesign/embedded gap. */
function BulkCreateCompanyReviewScreenWorkloads({
  manager,
}: {
  manager: CompanyShellWorkloadsManagerReturn;
}) {
  const navigate = useNavigate();
  const { state } = useCompanyProvisionWizard();
  // `lastAction` buckets the manager's single unified `managerError` into
  // the right banner slot — Track 1 has two separate hooks
  // (`validateInvocation.error`/`commitInvocation.error`); Track 2 has one
  // `managerError` covering every stage of the import→PATCH→validate
  // pipeline plus the commit/run stage (see the manager's module doc).
  const [lastAction, setLastAction] = useState<"validate" | "commit" | null>(
    null
  );

  // `BulkCreateCompanyWizardProviderWorkloads` seeds the manager as soon as
  // Upload sets `state.rows` — well before the user can navigate here — so
  // `manager.pendingRows` reflects the freshest upload in every realistic
  // navigation path. Falling back to `state.rows`/`state.columns` (context)
  // only covers the negligible pre-seed window.
  const rows = manager.hasData ? manager.pendingRows : state.rows;
  const columns = state.columns;

  const {
    data: templateVersion,
    isLoading: isTemplateVersionLoading,
    isError: isTemplateVersionError,
  } = useCompanyConfigVersion(
    state.template?.config_code,
    state.template?.version
  );

  useEffect(() => {
    if (manager.runId) {
      navigate("/onboarding/bulk-create-company/results");
    }
  }, [manager.runId, navigate]);

  const unknownColumns = useMemo(
    () => computeUnknownColumns(columns),
    [columns]
  );

  const errorMap = useMemo(
    () => computeErrorMap(rows, unknownColumns, manager.validateResult),
    [rows, unknownColumns, manager.validateResult]
  );

  const blockingIssueCount = useMemo(
    () =>
      computeBlockingIssueCount(
        manager.validateResult,
        unknownColumns,
        rows.length
      ),
    [manager.validateResult, unknownColumns, rows.length]
  );

  const handleCellChange = (
    rowArrayIndex: number,
    header: string,
    value: string
  ) => {
    const currentDisplay = formatCompanyShellRowForPreview(rows[rowArrayIndex]);
    const nextDisplay = { ...currentDisplay, [header]: value };
    const nextRow = buildCompanyShellRow((h) => nextDisplay[h] ?? "");
    manager.updateRow(rowArrayIndex, nextRow);
  };

  const handleValidate = () => {
    setLastAction("validate");
    manager.validate();
  };

  const handleCommit = () => {
    setLastAction("commit");
    manager.commit();
  };

  const isBusy = manager.isValidating || manager.isCommitting;

  const canValidate =
    !!state.template &&
    !!state.mypayEnvironment &&
    rows.length > 0 &&
    !manager.isValidating;
  const canCommit =
    !!state.template &&
    !!state.mypayEnvironment &&
    !!manager.validateResult &&
    // Warnings (severity === "warning") are informational — do not block Commit.
    !manager.validateResult.errors.some(
      (error) => error.severity !== "warning"
    ) &&
    // Undeclared CSV headers never reach the API — still block Commit
    // (AC-UPLOAD-12 / R-UPLOAD-2).
    unknownColumns.length === 0 &&
    // Edited since the last validate run — must re-validate before committing.
    !manager.validateStale &&
    !manager.isCommitting;

  const validateError = lastAction === "validate" ? manager.managerError : null;
  const commitError = lastAction === "commit" ? manager.managerError : null;

  if (rows.length === 0) {
    return (
      <div className={s.container}>
        <header className={s.header}>
          <h1 className={s.title}>Review</h1>
          <p className={s.subtitle}>
            No companies to review. Please upload a CSV first.
          </p>
        </header>
        <ButtonGroup align="right">
          <Button
            variant="tertiary"
            onPress={() => navigate("/onboarding/bulk-create-company")}
          >
            Back to upload
          </Button>
        </ButtonGroup>
      </div>
    );
  }

  return (
    <div className={s.container}>
      <header className={s.header}>
        <h1 className={s.title}>Review</h1>
        <p className={s.subtitle}>
          Edit any value before validating. The Checks column shows whether
          each row passed validation, and why it failed.
        </p>
      </header>

      <CompanyShellExtractionNotice provenance={state.extraction} />

      {validateError && (
        <p className={s.warning} role="alert">
          {validateError}
        </p>
      )}
      {commitError && (
        <p className={s.warning} role="alert">
          {commitError}
        </p>
      )}
      {manager.validateStale ? (
        <p className={s.warning} role="status">
          You&apos;ve edited rows since the last validation. The highlighted
          issues below reflect the previous values — re-run Validate before
          creating companies.
        </p>
      ) : (
        manager.validateResult && (
          <p
            className={blockingIssueCount > 0 ? s.warning : s.okBanner}
            role="status"
          >
            {blockingIssueCount > 0
              ? `${blockingIssueCount} issue${blockingIssueCount !== 1 ? "s" : ""} found across ${manager.validateResult.row_count} row(s).`
              : `All ${manager.validateResult.row_count} row(s) passed validation.`}
          </p>
        )
      )}
      {errorMap.batchErrors.length > 0 && (
        <div className={s.warning} role="alert">
          {errorMap.batchErrors.map((message, i) => (
            <p key={i}>{message}</p>
          ))}
        </div>
      )}

      <CompanyRowsTable
        columns={columns}
        rows={rows}
        editable={!isBusy}
        onCellChange={handleCellChange}
        cellErrors={manager.validateResult ? errorMap.cellErrors : undefined}
        errorsByRow={manager.validateResult ? errorMap.errorsByRow : undefined}
        normalisations={
          manager.validateResult
            ? manager.validateResult.normalisations
            : undefined
        }
        ariaLabel="Editable company rows"
      />

      {state.template && (
        <SelectedCompanyConfigTemplatePreview
          templateVersion={templateVersion}
          isLoading={isTemplateVersionLoading}
          isError={isTemplateVersionError}
          presentation="compact"
        />
      )}

      <ButtonGroup align="right">
        <Button
          variant="tertiary"
          onPress={() => navigate("/onboarding/bulk-create-company")}
        >
          Back to upload
        </Button>
        <Button
          variant="secondary"
          isDisabled={!canValidate}
          onPress={handleValidate}
        >
          {manager.isValidating ? "Validating…" : "Validate"}
        </Button>
        <Button variant="primary" isDisabled={!canCommit} onPress={handleCommit}>
          {manager.isCommitting ? "Committing…" : "Commit"}
        </Button>
      </ButtonGroup>
    </div>
  );
}
